import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import colors from "../../Constants/colors";
import { useEvaluationModel } from "../../Query/useEvaluation";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";

interface ModelAccessSelectorProps {
  selectedModelIds: number[];
  onChange: (modelIds: number[]) => void;
  disabled?: boolean;
  isMobile?: boolean;
  label?: string;
  // "card": no own title (the surrounding card has one), options in a grid.
  variant?: "default" | "card";
}

const ModelAccessSelector = ({
  selectedModelIds,
  onChange,
  disabled = false,
  isMobile = false,
  label,
  variant = "default",
}: ModelAccessSelectorProps) => {
  const { t } = useTranslation("createUser");
  const displayLabel = label || t("availableAiModels");
  const { data: modelData } = useEvaluationModel();
  const { colors: workspace } = useWorkspaceColors();

  const allModels = modelData?.all_models ?? [];

  const handleToggle = (modelId: number) => {
    if (selectedModelIds.includes(modelId)) {
      onChange(selectedModelIds.filter((id) => id !== modelId));
    } else {
      onChange([...selectedModelIds, modelId]);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {variant === "default" && (
        <Typography
          sx={{
            color: workspace.primary_dark,
            fontWeight: 600,
            fontSize: "0.85rem",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            borderBottom: `1px solid ${colors.gray200}`,
            pb: 0.5,
            mb: 1.5,
          }}
        >
          {displayLabel}
        </Typography>
      )}
      <FormControl component="fieldset" fullWidth disabled={disabled}>
        <FormGroup
          sx={
            variant === "card"
              ? {
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  columnGap: 2,
                }
              : undefined
          }
        >
          {allModels.map((model) => (
            <FormControlLabel
              key={model.model_id}
              control={
                <Checkbox
                  checked={selectedModelIds.includes(model.model_id)}
                  onChange={() => handleToggle(model.model_id)}
                  sx={{
                    color: colors.gray400,
                    "&.Mui-checked": { color: workspace.accent },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: colors.primary_dark,
                    fontSize: isMobile ? "0.95rem" : "0.875rem",
                  }}
                >
                  {model.model_name}
                </Typography>
              }
            />
          ))}
        </FormGroup>
      </FormControl>
    </Box>
  );
};

export default ModelAccessSelector;
